/* eslint-disable max-lines-per-function */
import { computed, ComputedRef, Ref, ref } from '@nuxtjs/composition-api';
// Imported from the types file directly (not the component barrel) to avoid a runtime import cycle,
// since the barrel now also exports SearchPreFiltersControl which type-imports this module.
import type {
  IFacetOptionsBySection,
  IPreFilterOption,
  IPreFilterTab,
  ISearchPreFiltersConfig,
  ISearchPreFiltersValue,
} from '@shared/components/SearchPreFilters/types';
import getSearchQueryLink from '@shared/composables/useUiHelpers/getSearchQueryLink';
import { ISearchResponse, useSearch } from '@unified-commerce/gpc-vue-storefront-search';
import { RawLocation } from 'vue-router';

// Brand-specific constants the engine needs. Every brand already defines these (most as shared
// defaults, SEARCH_CATEGORY_TENANT_PREFIX per-brand), so a brand just passes its own in.
export interface ISearchPreFiltersConstants {
  BASE_SEARCH_PATH: string;
  HIDDEN_PRODUCT_TAG: string;
  SEARCH_CATEGORY_TENANT_PREFIX: string;
  SEARCH_DEFAULT_TITLE_OPTION: string;
  SEARCH_QUERY_PARAM: string;
}

export interface ICreateSearchPreFiltersOptions {
  config: ISearchPreFiltersConfig;
  // facetField -> aggregate key the search composable returns it under (brand's composableConfig).
  facetMaps: Record<string, string>;
  constants: ISearchPreFiltersConstants;
}

// Shape returned by a brand's `useSearchPreFilters()`. Exposed so the shared SearchPreFiltersControl
// can accept the brand's singleton as a typed prop.
export interface ISearchPreFiltersController {
  showPreFilters: Ref<boolean>;
  loading: Ref<boolean>;
  facetOptions: Ref<IFacetOptionsBySection>;
  selectedFilters: Ref<ISearchPreFiltersValue>;
  selectedTabIndex: Ref<number | null>;
  activeFilterCount: ComputedRef<number>;
  searchPreFiltersConfig: ISearchPreFiltersConfig;
  openPreFilters: () => void;
  closePreFilters: () => void;
  clearFilters: () => void;
  setFilters: (filters: ISearchPreFiltersValue) => void;
  commitContext: (filters: ISearchPreFiltersValue, tabIndex: number) => void;
  buildQuery: (filters: ISearchPreFiltersValue, activeTabIndex?: number) => Record<string, string>;
  buildContextualSearchQuery: (term: string) => RawLocation;
}

// Builds a brand's `useSearchPreFilters` composable from its config. Call once at module scope in
// `websites/<brand>/composables/useSearchPreFilters.ts` so the returned refs form a per-brand
// singleton (same in-memory-context pattern as useMyGarage) shared by SearchBar and the layout.
export function createSearchPreFilters({
  config,
  facetMaps,
  constants,
}: ICreateSearchPreFiltersOptions) {
  const {
    BASE_SEARCH_PATH,
    HIDDEN_PRODUCT_TAG,
    SEARCH_CATEGORY_TENANT_PREFIX,
    SEARCH_DEFAULT_TITLE_OPTION,
    SEARCH_QUERY_PARAM,
  } = constants;

  const BASE_FILTERS = `NOT tags:"${HIDDEN_PRODUCT_TAG}" AND collections:${SEARCH_CATEGORY_TENANT_PREFIX}`;

  // Collection handles are prefixed with the tenant; the `subcategories` query param expects them without it.
  const COLLECTION_HANDLE_PREFIX = `${SEARCH_CATEGORY_TENANT_PREFIX}-`;

  // Narrows a facet query to a tab's collection(s), OR'ing multiple together.
  const tabFacetFilters = (tab: IPreFilterTab): string => {
    const handles = tab.collectionHandles ?? [];
    if (handles.length === 0) return BASE_FILTERS;
    const clause = handles.map((handle) => `collections:"${handle}"`).join(' OR ');
    return `${BASE_FILTERS} AND (${clause})`;
  };

  // Converts a tab's collection handles into `subcategories` tokens (handle minus tenant prefix).
  const tabSubcategoryTokens = (tab: IPreFilterTab): string[] =>
    (tab.collectionHandles ?? []).map((handle) =>
      handle.startsWith(COLLECTION_HANDLE_PREFIX)
        ? handle.slice(COLLECTION_HANDLE_PREFIX.length)
        : handle,
    );

  const readFacetOptions = (
    response: ISearchResponse | null,
    facetField: string,
  ): IPreFilterOption[] => {
    const aggregateKey = facetMaps[facetField];
    const counts = aggregateKey
      ? response?.aggregateFilters?.[aggregateKey]?.count?.counts ?? {}
      : {};

    return Object.entries(counts)
      .filter(([title]) => title && title !== SEARCH_DEFAULT_TITLE_OPTION)
      .map(([title, count]) => ({ value: title, label: title, count: count as number }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  // Module-level singleton state (within this brand's bundle) so both SearchBar (which drives the
  // modal) and the layout's free-text search handler share one in-memory pre-filter context.
  const showPreFilters = ref(false);
  const loading = ref(false);
  const loaded = ref(false);
  const facetOptions = ref<IFacetOptionsBySection>({});
  const selectedFilters = ref<ISearchPreFiltersValue>({});
  // The tab whose category + facets contextualise free-text search. null = no committed context.
  const selectedTabIndex = ref<number | null>(null);

  // One useSearch instance per tab, created lazily inside a component setup context (not at import).
  let tabSearches: ReturnType<typeof useSearch>[] | null = null;

  const activeFilterCount = computed(() =>
    Object.entries(selectedFilters.value).reduce((count, [key, value]) => {
      if (key === 'priceMin' || key === 'priceMax') {
        return value !== null && value !== undefined ? count + 1 : count;
      }
      return Array.isArray(value) ? count + value.length : count;
    }, 0),
  );

  const fetchTabFacets = async (
    tab: IPreFilterTab,
    index: number,
  ): Promise<IFacetOptionsBySection> => {
    if (!tabSearches) return {};
    const fields = [...new Set(tab.sections.map((section) => section.facetField))];
    const { search, result } = tabSearches[index];

    await search({
      query: '',
      hitsPerPage: 1,
      facets: fields,
      facetMaps,
      filters: tabFacetFilters(tab),
    });

    return tab.sections.reduce<IFacetOptionsBySection>((bySection, section) => {
      bySection[section.name] = readFacetOptions(result.value, section.facetField);
      return bySection;
    }, {});
  };

  const loadFacets = async () => {
    if (loaded.value || loading.value) return;
    loading.value = true;
    try {
      const results = await Promise.all(
        config.tabs.map((tab, index) => fetchTabFacets(tab, index)),
      );
      facetOptions.value = Object.assign({}, ...results);
      loaded.value = true;
    } catch {
      // Degrade gracefully: leave options empty so the dialog still renders, and
      // allow a retry the next time it is opened (loaded stays false).
      facetOptions.value = {};
    } finally {
      loading.value = false;
    }
  };

  const openPreFilters = () => {
    showPreFilters.value = true;
    loadFacets();
  };

  const closePreFilters = () => {
    showPreFilters.value = false;
  };

  const clearFilters = () => {
    selectedFilters.value = {};
    selectedTabIndex.value = null;
  };

  const setFilters = (filters: ISearchPreFiltersValue) => {
    selectedFilters.value = filters;
  };

  // Commits the user's in-dialog selection as the persisted free-text-search context. Fired on any
  // modal interaction (facet/price change or tab switch), so the context survives closing with the X.
  const commitContext = (filters: ISearchPreFiltersValue, tabIndex: number) => {
    selectedFilters.value = filters;
    selectedTabIndex.value = tabIndex;
  };

  // Converts the active tab's selections into the search page's query params. Only the active tab is
  // applied: its category scopes the search (via `subcategories`) and its facet selections become
  // filters, so selections left on other (hidden) category tabs never leak into another category.
  const buildQuery = (
    filters: ISearchPreFiltersValue,
    activeTabIndex = 0,
  ): Record<string, string> => {
    const query: Record<string, string> = {};
    const activeTab = config.tabs[activeTabIndex];
    if (!activeTab) return query;

    const paramValues: Record<string, string[]> = {};
    activeTab.sections.forEach((section) => {
      const selection = filters[section.name];
      const queryParam = section.queryParam;
      if (!queryParam || !Array.isArray(selection) || selection.length === 0) return;

      paramValues[queryParam] = [...new Set([...(paramValues[queryParam] ?? []), ...selection])];
    });

    Object.entries(paramValues).forEach(([param, values]) => {
      query[param] = values.join(',');
    });

    const subcategoryTokens = tabSubcategoryTokens(activeTab);
    if (subcategoryTokens.length > 0) {
      query.subcategories = subcategoryTokens.join(',');
    }

    const { priceMin, priceMax } = filters;
    if (priceMin != null && priceMax != null) {
      query['price-range'] = `$${priceMin}-$${priceMax}`;
    } else if (priceMin != null) {
      query['price-range'] = `above-$${priceMin}`;
    } else if (priceMax != null) {
      query['price-range'] = `up-to-$${priceMax}`;
    }

    return query;
  };

  // Builds the destination for a free-text term, merged with the committed pre-filter context.
  // With no context it falls back to today's behaviour (`/search` or `/search?q=...`).
  const buildContextualSearchQuery = (term: string): RawLocation => {
    const q = (term ?? '').trim();
    if (selectedTabIndex.value === null) {
      return getSearchQueryLink(q);
    }
    return {
      path: BASE_SEARCH_PATH,
      query: {
        ...(q ? { [SEARCH_QUERY_PARAM]: q } : {}),
        ...buildQuery(selectedFilters.value, selectedTabIndex.value),
      },
    };
  };

  return function useSearchPreFilters(): ISearchPreFiltersController {
    if (!tabSearches) {
      tabSearches = config.tabs.map((_, index) => useSearch(`search-prefilters-tab-${index}`));
    }

    return {
      showPreFilters,
      loading,
      facetOptions,
      selectedFilters,
      selectedTabIndex,
      activeFilterCount,
      searchPreFiltersConfig: config,
      openPreFilters,
      closePreFilters,
      clearFilters,
      setFilters,
      commitContext,
      buildQuery,
      buildContextualSearchQuery,
    };
  };
}
